import React, { useState, useEffect } from 'react';
import { X, Search, Loader, Link as LinkIcon, CreditCard } from 'lucide-react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { format } from 'date-fns';
import { parseLocalDate } from '../utils/timeFormat';

const API_URL = process.env.REACT_APP_API_URL || '/api';

const SquarePaymentLinkModal = ({ isOpen, onClose, appointment, onSuccess }) => {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [linking, setLinking] = useState(false);
  const [filters, setFilters] = useState({
    startDate: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0],
    minAmount: appointment?.total_price ? (appointment.total_price - 5).toFixed(2) : '',
    maxAmount: appointment?.total_price ? (appointment.total_price + 5).toFixed(2) : '',
  });

  useEffect(() => {
    if (isOpen && appointment) {
      searchPayments();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, appointment]);

  const searchPayments = async () => {
    setLoading(true);
    try {
      const token = sessionStorage.getItem('token');
      const params = new URLSearchParams();
      
      if (filters.startDate) params.append('startDate', new Date(filters.startDate).toISOString());
      if (filters.endDate) params.append('endDate', new Date(filters.endDate + 'T23:59:59').toISOString());
      if (filters.minAmount) params.append('minAmount', filters.minAmount);
      if (filters.maxAmount) params.append('maxAmount', filters.maxAmount);

      const response = await axios.get(`${API_URL}/payments/square-payments?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setPayments(response.data.payments || []);
    } catch (error) {
      console.error('Search payments error:', error);
      toast.error('Failed to load Square payments');
    } finally {
      setLoading(false);
    }
  };

  const handleLinkPayment = async (payment) => {
    setLinking(true);
    try {
      const token = sessionStorage.getItem('token');
      await axios.post(
        `${API_URL}/payments/link-square-payment`,
        {
          appointmentId: appointment.id,
          squarePaymentId: payment.id,
          amount: payment.amount,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success('Payment linked successfully!');
      if (onSuccess) onSuccess();
      onClose();
    } catch (error) {
      console.error('Link payment error:', error);
      toast.error('Failed to link payment');
    } finally {
      setLinking(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-6 border-b">
          <div className="flex items-center space-x-2">
            <LinkIcon className="text-primary-600" size={24} />
            <h2 className="text-xl font-bold">Link Square Payment</h2>
          </div>
          <button
            onClick={onClose}
            disabled={linking}
            className="text-gray-400 hover:text-gray-600"
          >
            <X size={24} />
          </button>
        </div>

        <div className="p-6 border-b bg-gray-50">
          <div className="mb-4">
            <h3 className="font-semibold text-gray-900 mb-2">Appointment Details</h3>
            <p className="text-sm text-gray-600">
              Amount: <span className="font-bold">${appointment?.total_price?.toFixed(2)}</span>
              {' • '}
              Date: {appointment?.appointment_date && format(parseLocalDate(appointment.appointment_date), 'MMM d, yyyy')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
              <input
                type="date"
                value={filters.startDate}
                onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
                className="input text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
              <input
                type="date"
                value={filters.endDate}
                onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
                className="input text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Min Amount</label>
              <input
                type="number"
                step="0.01"
                value={filters.minAmount}
                onChange={(e) => setFilters({ ...filters, minAmount: e.target.value })}
                className="input text-sm"
                placeholder="0.00"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Max Amount</label>
              <input
                type="number"
                step="0.01"
                value={filters.maxAmount}
                onChange={(e) => setFilters({ ...filters, maxAmount: e.target.value })}
                className="input text-sm"
                placeholder="0.00"
              />
            </div>
          </div>

          <button
            onClick={searchPayments}
            disabled={loading}
            className="mt-4 btn btn-primary"
          >
            <Search size={16} className="mr-2" />
            {loading ? 'Searching...' : 'Search Payments'}
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {loading && (
            <div className="flex flex-col items-center justify-center py-12">
              <Loader className="animate-spin text-primary-600 mb-4" size={32} />
              <p className="text-gray-600">Loading payments...</p>
            </div>
          )}

          {!loading && payments.length === 0 && (
            <div className="text-center py-12">
              <CreditCard className="mx-auto text-gray-400 mb-4" size={48} />
              <p className="text-gray-600">No payments found matching your criteria</p>
              <p className="text-sm text-gray-500 mt-2">Try adjusting your search filters</p>
            </div>
          )}

          {!loading && payments.length > 0 && (
            <div className="space-y-3">
              {payments.map((payment) => (
                <div
                  key={payment.id}
                  className="border-2 rounded-lg p-4 hover:border-primary-300 transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <span className="text-2xl font-bold text-gray-900">
                          ${payment.amount.toFixed(2)}
                        </span>
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          payment.status === 'COMPLETED' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                        }`}>
                          {payment.status}
                        </span>
                      </div>
                      <div className="text-sm text-gray-600 space-y-1">
                        <p>
                          <span className="font-medium">Date:</span>{' '}
                          {format(new Date(payment.createdAt), 'MMM d, yyyy h:mm a')}
                        </p>
                        {payment.cardBrand && payment.last4 && (
                          <p>
                            <span className="font-medium">Card:</span>{' '}
                            {payment.cardBrand} •••• {payment.last4}
                          </p>
                        )}
                        {payment.note && (
                          <p>
                            <span className="font-medium">Note:</span> {payment.note}
                          </p>
                        )}
                        <p className="text-xs text-gray-500">ID: {payment.id}</p>
                      </div>
                    </div>
                    <button
                      onClick={() => handleLinkPayment(payment)}
                      disabled={linking || payment.status !== 'COMPLETED'}
                      className="btn btn-primary ml-4"
                    >
                      <LinkIcon size={16} className="mr-2" />
                      Link
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="p-6 border-t bg-gray-50">
          <button
            onClick={onClose}
            disabled={linking}
            className="btn btn-secondary w-full"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default SquarePaymentLinkModal;
